import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { ChevronUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Public list of questions for a session — visible to all ticket-holders.
 * Each item shows vote count + upvote button. Auto-refreshes every 15s.
 * Submitter identity is hidden (the chair still sees it on their console).
 *
 * Props:
 *   - sessionId (required)
 *   - pollMs (default 15000)
 *   - emptyText (optional)
 */
export default function SessionQuestionsList({ sessionId, pollMs = 15000, emptyText }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState({});

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/sessions/${sessionId}/questions`);
      setQuestions(r.data.questions || []);
    } catch {
      // Silent — list is best-effort
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  const vote = async (qid) => {
    setVoting((p) => ({ ...p, [qid]: true }));
    // Optimistic update so the button feels instant
    setQuestions((arr) => arr.map((q) =>
      q.question_id === qid
        ? { ...q, viewer_has_voted: !q.viewer_has_voted, vote_count: q.vote_count + (q.viewer_has_voted ? -1 : 1) }
        : q,
    ));
    try {
      await api.post(`/sessions/${sessionId}/questions/${qid}/vote`);
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    } finally {
      setVoting((p) => ({ ...p, [qid]: false }));
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 px-1" data-testid="questions-list-loading">Loading questions…</div>;
  }

  if (questions.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-50 px-4 py-5 text-center text-sm text-slate-500" data-testid="questions-list-empty">
        {emptyText || "No questions yet. Be the first to ask one!"}
      </div>
    );
  }

  return (
    <ul className="space-y-2" data-testid="questions-list">
      {questions.map((q) => (
        <li
          key={q.question_id}
          className={`flex gap-3 rounded-2xl px-3 py-3 ring-1 ${
            q.status === "asked"
              ? "bg-emerald-50/60 ring-emerald-100"
              : "bg-white ring-slate-100"
          }`}
          data-testid={`question-${q.question_id}`}
        >
          <button
            type="button"
            onClick={() => vote(q.question_id)}
            disabled={q.is_mine || !!voting[q.question_id]}
            title={q.is_mine ? "You can't vote on your own question" : (q.viewer_has_voted ? "Remove your vote" : "Upvote")}
            data-testid={`vote-${q.question_id}`}
            className={`shrink-0 inline-flex flex-col items-center justify-center w-11 rounded-xl px-1.5 py-1 transition ${
              q.viewer_has_voted
                ? "bg-[#28BBE3] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            } ${q.is_mine ? "opacity-50 cursor-default" : ""}`}
          >
            <ChevronUp size={15} strokeWidth={2.4} />
            <span className="text-xs font-semibold leading-none mt-0.5">{q.vote_count}</span>
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-800 leading-relaxed">{q.text}</p>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
              {q.is_mine && <span className="text-slate-500 font-medium">Yours</span>}
              {q.status === "asked" && (
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                  <CheckCircle2 size={11} /> Asked by chair
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
